import * as React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { initials } from '../lib/initials'
import { cn } from '../lib/utils'

/** Numeric Tailwind size tokens for the avatar. Default (6) matches the pre-existing `size-6`. */
const SIZE_CLASS = {
  5: 'size-5 text-[9px]',
  6: 'size-6 text-[0.6rem]',
  7: 'size-7 text-[11px]',
  8: 'size-8 text-[12px]',
  9: 'size-9 text-xs',
  14: 'size-14 text-base',
} as const

export interface PersonAvatarProps {
  /** Resolved display name — used for the initials fallback and the aria-label. */
  label: string
  /** Profile image URL; null/undefined (or a load failure) falls back to initials. */
  imageUrl?: string | null
  /** Size/spacing overrides — defaults to `size-6` (24px). */
  className?: string
  /** Numeric size token — defaults to 6 (24px). `className` merges after, so it can still override. */
  size?: keyof typeof SIZE_CLASS
  /**
   * When set, the initials fallback gets a deterministic OKLCH hue derived
   * from this seed (e.g. a person id) instead of the default neutral
   * styling — lets a list of avatars read as a distinct-but-consistent palette.
   */
  colorSeed?: string
}

/** `(h * 31 + charCode) | 0` rolling hash, spread across the hue wheel. */
function hashHue(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return Math.abs(h) % 360
}

/**
 * Circular avatar: profile image when `imageUrl` resolves, else uppercase
 * initials. Clerk-agnostic by design — callers pass a plain label + URL, so
 * any Loop3 app can reuse it. forwardRef is load-bearing, same reason as `Avatar`.
 */
export const PersonAvatar = React.forwardRef<HTMLSpanElement, PersonAvatarProps>(
  function PersonAvatar({ label, imageUrl, className, size = 6, colorSeed }, ref) {
    const hue = colorSeed != null ? hashHue(colorSeed) : null
    return (
      <Avatar
        ref={ref}
        aria-label={label}
        role="img"
        className={cn(SIZE_CLASS[size], className)}
      >
        {imageUrl ? <AvatarImage src={imageUrl} alt="" /> : null}
        <AvatarFallback
          className="font-mono uppercase"
          style={
            hue != null
              ? { background: `oklch(0.93 0.05 ${hue})`, color: `oklch(0.32 0.14 ${hue})` }
              : undefined
          }
        >
          {initials(label)}
        </AvatarFallback>
      </Avatar>
    )
  }
)
